import { useState, useEffect, useContext, Fragment } from 'react';
import { Grid, Box, IconButton, Button, LinearProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttFormSelect,
    SttFormTextarea,
    SttFormText,
    SttFormCheck,
} from '../../../sporttia/all';
import {
    deleteElementFromArray,
    updateElementInArray,
} from '../../../lib/utils';
import timeImg from '../../../images/time.svg';

type Conversion = {
    id: number | string;
    origin: number | string;
    destine: string;
};

type Props = {
    open?: boolean;
    file: any;
    lines: any;
    onClose: () => void;
    onUpdate?: (file: any) => void;
};

export default function ImportUsersDialog({
    open = false,
    file,
    lines,
    onClose,
    onUpdate,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [importing, setImporting] = useState<boolean>(false);
    const [notes, setNotes] = useState<string>();
    const [numUsersToImport, setNumUsersToImport] = useState<number>(1);
    const [modifyExisting, setModifyExisting] = useState<boolean>(false);

    /**
     *	1 => login
     *
     *	means that the column 1 of the list goes as "login" of user
     */
    const [conversions, setConversions] = useState<Conversion[]>([
        /*
		{
			origin: 1,
			destine: 'login'
		}
		*/
    ]);

    const fieldNumbers = [
        ...Array(cxt?.constants?.numFieldsToUserImport || 0).keys(),
    ];
    const fieldConversions = [
        { value: 'login', caption: 'Login' },
        { value: 'name', caption: t('Name') },
        { value: 'surname1', caption: `${t('Surname')} 1` },
        { value: 'surname2', caption: `${t('Surname')} 2` },
        { value: 'password', caption: t('Password') },
        { value: 'dni', caption: t('DNI') },
        { value: 'address', caption: t('Address') },
        { value: 'phone', caption: t('Mobile') },
        { value: 'email', caption: t('Email') },
        { value: 'iban', caption: t('IBAN') },
        { value: 'idCity', caption: `${t('City')} (id)` },
        { value: 'idGroup', caption: `${t('Group')} (id)` },
        { value: 'purseAmount', caption: t('purse') },
        { value: 'birthday', caption: t('Birthday') },
        { value: 'gender', caption: t('Gender') },
    ];

    /**
     * Do import
     */
    const doImport = () => {
        setImporting(true);
        cxt?.api('POST', `/files/csvs/${file.id}/users`, {
            params: {
                conversions: conversions.map((conv: any) => ({
                    origin: conv.origin === 'B' ? 0 : conv.origin,
                    destine: conv.destine,
                })),
                notes,
                numUsersToImport,
                modifyExisting,
            },
            success: (r: any) => {
                cxt?.showMessage('S', t('importUsers.success'));
                onClose();
                if (onUpdate) onUpdate(r.fileCSV);
                setImporting(false);
            },
        });
    };

    /**
     * Add conversion
     */
    const addConversion = () => {
        setConversions([
            ...conversions,
            {
                id: conversions.length ? conversions.length : 'B',
                origin: conversions.length ? conversions.length : 'B',
                destine: fieldConversions[0]?.value || 'login',
            },
        ]);
    };

    /**
     * Remove conversion
     */
    const removeConversion = (conv: Conversion) => {
        const result = deleteElementFromArray(conversions, conv);
        setConversions(result || []);
    };

    /**
     * Update conversion
     */
    const updateConversion = (conversion: Conversion) => {
        setConversions(updateElementInArray(conversions, conversion));
    };

    /**
     * File changes
     */
    useEffect(() => {
        setNotes(
            `${file && file.name} - Import ${format(
                new Date(),
                'yyyy-MM-dd HH:mm:ss',
            )}`,
        );
    }, [file]);

    /**
     * Init
     */
    useEffect(() => {
        addConversion();
    }, []);

    // -----| Render |-----
    return (
        <SttDialog
            maxWidth="lg"
            title={`${t('ImportUsers')}: ${lines && lines.count}`}
            open={open}
            content={
                <>
                    {importing ? (
                        <Box p={2} sx={{ textAlign: 'center' }}>
                            <Box mb={2}>
                                <img src={timeImg} width={130} />
                            </Box>
                            <LinearProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {/* --- LEFT COLUMN: Conversion list --- */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Grid container spacing={2}>
                                    {conversions.map((conv, idx) => (
                                        <Fragment key={idx}>
                                            <SttFormSelect
                                                grid
                                                md={12}
                                                sm={12}
                                                xs={12}
                                                caption={t('Origin')}
                                                defVal={conv.origin}
                                                onChange={({
                                                    value,
                                                }: {
                                                    name: string | undefined;
                                                    value: unknown;
                                                }) =>
                                                    updateConversion({
                                                        id: conv.id,
                                                        origin:
                                                            (value as
                                                                | number
                                                                | string) ||
                                                            'B',
                                                        destine: conv.destine,
                                                    })
                                                }
                                                options={fieldNumbers.map(
                                                    (num: any) => ({
                                                        caption: `#${num}`,
                                                        value: num || 'B',
                                                    }),
                                                )}
                                            />

                                            <SttFormSelect
                                                grid
                                                md={10}
                                                sm={10}
                                                xs={10}
                                                caption={t('Destination')}
                                                defVal={conv.destine}
                                                onChange={({
                                                    value,
                                                }: {
                                                    name: string | undefined;
                                                    value: unknown;
                                                }) =>
                                                    updateConversion({
                                                        id: conv.id,
                                                        origin: conv.origin,
                                                        destine:
                                                            value as string,
                                                    })
                                                }
                                                options={fieldConversions}
                                            />

                                            <Grid size={{ xs: 2, md: 2 }}>
                                                <IconButton
                                                    onClick={() =>
                                                        removeConversion(conv)
                                                    }
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            </Grid>
                                        </Fragment>
                                    ))}

                                    {/* --- Add new conversion --- */}
                                    <Grid size={12}>
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={() => addConversion()}
                                        >
                                            {t('New')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* --- RIGHT COLUMN: Info --- */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Grid container spacing={2}>
                                    {/* --- Rules text --- */}
                                    <Grid size={12}>
                                        <Box mb={1}>
                                            {t('importUsers.rules')}
                                        </Box>
                                    </Grid>

                                    {/* --- Notes --- */}
                                    <SttFormTextarea
                                        grid
                                        md={12}
                                        sm={12}
                                        xs={12}
                                        name="notes"
                                        caption={t('Notes')}
                                        defVal={notes}
                                        onChange={({
                                            value,
                                        }: {
                                            name: string | undefined;
                                            value: unknown;
                                        }) => setNotes(value as string)}
                                    />

                                    {/* --- Num users to import --- */}
                                    <SttFormText
                                        grid
                                        md={6}
                                        sm={6}
                                        xs={12}
                                        name="numUsersToImport"
                                        caption={t('UsersToImport')}
                                        defVal={String(numUsersToImport)}
                                        onChange={({
                                            value,
                                        }: {
                                            name: string | undefined;
                                            value: unknown;
                                        }) =>
                                            setNumUsersToImport(Number(value))
                                        }
                                    />

                                    {/* --- Modify existing while importing --- */}
                                    <SttFormCheck
                                        grid
                                        md={6}
                                        sm={6}
                                        xs={12}
                                        name="modifyExisting"
                                        caption={t(
                                            'importUsers.modifyExisting',
                                        )}
                                        checked={modifyExisting}
                                        onChange={({
                                            value,
                                        }: {
                                            name: string | undefined;
                                            value: unknown;
                                        }) =>
                                            setModifyExisting(value as boolean)
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </>
            }
            buttons={[
                {
                    show: !importing,
                    type: 'accept',
                    caption: t('ImportUsers'),
                    confirmation: true,
                    onClick: () => doImport(),
                },
            ]}
        />
    );
}
